import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDsTranslate, useDsServices, useDsNotify } from '../context/DsProvider';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { formatTime, formatDate } from '../utils/dateFormat';
import type { DocumentReview, ReviewActivity, ReviewAction } from '../types/review';
import styles from './ChatSidebar.module.scss';

interface ChatSidebarProps {
  targetType: string;
  targetId: string;
  reviews: DocumentReview[];
  /**
   * - `author`: workpay author, session-authed, uses `reviewService.addNote`
   * - `reviewer`: external reviewer, token-authed, uses `reviewPublic.addReviewerNote`
   * - `reviewer-internal`: session-authed INTERNAL reviewer (tenant user or workpay colleague),
   *   uses `reviewService.addReviewerNote(reviewId, message)`. Requires `reviewId` prop.
   */
  mode: 'author' | 'reviewer' | 'reviewer-internal';
  /** Required for `mode='reviewer'` (external token). */
  token?: string;
  /** Required for `mode='reviewer-internal'` (session-authed internal reviewer). */
  reviewId?: string;
  /**
   * Callback fired after a successful note send. When provided, the widget
   * SKIPS its built-in cache-invalidation (hardcoded to the author/token key
   * structure) and lets the caller own invalidation. Use this from consumers
   * whose query keys don't match the built-in ones.
   */
  onNoteSent?: () => void;
  hideHeader?: boolean;
}

interface ChatEntry {
  type: 'message' | 'system' | 'divider';
  id: string;
  actorName?: string;
  actorType?: string;
  action?: ReviewAction;
  message?: string;
  createdOn: string;
  reviewCycle?: number;
}

const SYSTEM_ACTIONS: Set<string> = new Set(['REVIEW_STARTED', 'MARKED_COMPLETE']);
const MESSAGE_ACTIONS: Set<string> = new Set(['CHANGES_SUBMITTED', 'CHANGES_RESPONDED', 'NOTE_ADDED']);

function getActionLabel(action: string, t: (key: string, fallback: string) => string): string {
  switch (action) {
    case 'CHANGES_SUBMITTED': return t('K_REVIEW_ACTION_CHANGES_SUBMITTED', 'Submitted changes');
    case 'CHANGES_RESPONDED': return t('K_REVIEW_ACTION_RESPONDED', 'Responded');
    case 'NOTE_ADDED': return t('K_REVIEW_ACTION_NOTE_ADDED', 'Added a note');
    default: return action;
  }
}

function buildChatEntries(reviews: DocumentReview[]): ChatEntry[] {
  const entries: ChatEntry[] = [];
  reviews.forEach((review, reviewIndex) => {
    if (reviewIndex > 0) {
      entries.push({ type: 'divider', id: `divider-${review.id}`, createdOn: review.createdOn, reviewCycle: reviewIndex + 1 });
    }
    review.activities.forEach((activity: ReviewActivity) => {
      const isSystem = SYSTEM_ACTIONS.has(activity.action);
      const isMessage = MESSAGE_ACTIONS.has(activity.action) || !isSystem;
      entries.push({
        type: isSystem ? 'system' : isMessage ? 'message' : 'system',
        id: activity.id, actorName: activity.actorName, actorType: activity.actorType,
        action: activity.action, message: activity.message, createdOn: activity.createdOn, reviewCycle: reviewIndex + 1,
      });
    });
  });
  return entries;
}

function getSystemEventText(action: ReviewAction, actorName: string, t: (key: string, fallback: string) => string): string {
  switch (action) {
    case 'REVIEW_STARTED': return t('K_REVIEW_EVENT_STARTED', '{name} started the review').replace('{name}', actorName);
    case 'MARKED_COMPLETE': return t('K_REVIEW_EVENT_COMPLETED', '{name} marked the review as complete').replace('{name}', actorName);
    default: return `${actorName} — ${action}`;
  }
}

export function ChatSidebar({
  targetType, targetId, reviews, mode, token, reviewId, onNoteSent, hideHeader = false,
}: ChatSidebarProps) {
  const t = useDsTranslate();
  const queryClient = useQueryClient();
  const notify = useDsNotify();
  const { review: reviewService, reviewPublic: reviewPublicService } = useDsServices();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [noteText, setNoteText] = useState('');

  const chatEntries = useMemo(() => buildChatEntries(reviews), [reviews]);

  const activeReview = useMemo(() => {
    return reviews.find((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING') ?? reviews[0] ?? null;
  }, [reviews]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatEntries.length]);

  const addNoteMutation = useMutation({
    mutationFn: async (message: string) => {
      if (mode === 'author') {
        if (!activeReview || !reviewService) throw new Error('No active review or service');
        return reviewService.addNote(activeReview.id, message);
      } else if (mode === 'reviewer-internal') {
        if (!reviewId || !reviewService) throw new Error('No reviewId or review service');
        return reviewService.addReviewerNote(reviewId, message);
      } else {
        if (!token || !reviewPublicService) throw new Error('No token or reviewPublic service');
        return reviewPublicService.addReviewerNote(token, message);
      }
    },
    onSuccess: () => {
      // Caller-owned invalidation path — when onNoteSent is passed, consumer
      // is responsible for refreshing their own query cache. Avoids stale
      // hardcoded invalidations colliding with non-matching consumer keys.
      if (onNoteSent) {
        onNoteSent();
      } else {
        void queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
        if (token) {
          void queryClient.invalidateQueries({ queryKey: ['ds-review-history', token] });
          void queryClient.invalidateQueries({ queryKey: ['ds-review-activities', token] });
        }
      }
      setNoteText('');
    },
    onError: () => {
      notify({ variant: 'critical', title: t('K_REVIEW_NOTE_ERROR', 'Failed to send message.') });
    },
  });

  const handleSendNote = useCallback(() => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    addNoteMutation.mutate(trimmed);
  }, [noteText, addNoteMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendNote(); }
    },
    [handleSendNote],
  );


  return (
    <div className={styles.sidebar}>
      {!hideHeader && (
        <div className={styles.header}>
          <h3 className={styles.headerTitle}>{t('K_REVIEW_CHAT_TITLE', 'Review Discussion')}</h3>
        </div>
      )}

      <div className={styles.messages}>
        {chatEntries.length === 0 && (
          <div className={styles.empty}>{t('K_REVIEW_NO_MESSAGES', 'No messages yet.')}</div>
        )}

        {chatEntries.map((entry) => {
          if (entry.type === 'divider') {
            return (
              <div key={entry.id} className={styles.sessionDivider}>
                <span className={styles.sessionDividerText}>
                  {t('K_REVIEW_CYCLE', 'Review Cycle {n}').replace('{n}', String(entry.reviewCycle ?? ''))}
                  {' — '}{formatDate(entry.createdOn)}
                </span>
              </div>
            );
          }

          if (entry.type === 'system') {
            return (
              <div key={entry.id} className={styles.systemEvent}>
                <span className={styles.systemDot} />
                <span>
                  {getSystemEventText(entry.action!, entry.actorName ?? '', t)}
                  {entry.message && ` — "${entry.message}"`}
                  {' — '}{formatTime(entry.createdOn)}
                </span>
              </div>
            );
          }

          const isAuthor = entry.actorType === 'AUTHOR';
          return (
            <div key={entry.id} className={`${styles.message} ${isAuthor ? styles.messageAuthor ?? '' : ''}`}>
              <div className={styles.messageHeader}>
                <span className={styles.messageName}>{entry.actorName}</span>
                <span className={styles.messageTime}>{formatTime(entry.createdOn)}</span>
              </div>
              <div className={styles.messageBody}>
                {entry.message || getActionLabel(entry.action ?? '', t)}
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {reviews.length > 0 && (
        <div className={styles.inputArea}>
          <Input
            placeholder={t('K_PLACEHOLDER_REVIEW_NOTE', 'Type a message...')}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleSendNote}
            disabled={!noteText.trim()}
            loading={addNoteMutation.isPending}
          >
            {t('K_BUTTON_SEND', 'Send')}
          </Button>
        </div>
      )}
    </div>
  );
}
